import React, { createContext, useContext, useState } from 'react';
import { PrimaryFeature, SecondaryFeature, TertiaryFeature } from 'utils/types';
import {
  PrimaryFeatureDto,
  SecondaryFeatureDto,
  TertiaryFeatureDto,
} from '../utils/principal-service/dto/GetFeatures.dto';
import { usePrincipalFeatures } from '../hooks/usePrincipalFeatures';
import { usePrincipals } from '../hooks/queries';
import { getCurrentPrincipal } from '../lib/currentPrincipal';

export const NavigationContext = createContext<{
  primaryFeature: PrimaryFeatureDto | null;
  setPrimaryFeature: (
    feature: PrimaryFeatureDto | PrimaryFeature | null,
  ) => void;
  secondaryFeature: SecondaryFeatureDto | null;
  setSecondaryFeature: (
    feature: SecondaryFeatureDto | SecondaryFeature | null,
  ) => void;
  tertiaryFeature: TertiaryFeatureDto | null;
  setTertiaryFeature: (
    feature: TertiaryFeatureDto | TertiaryFeature | null,
  ) => void;
}>({
  primaryFeature: null,
  setPrimaryFeature: () => undefined,
  secondaryFeature: null,
  setSecondaryFeature: () => undefined,
  tertiaryFeature: null,
  setTertiaryFeature: () => undefined,
});

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [primaryFeature, setRawPrimaryFeature] =
    useState<PrimaryFeatureDto | null>(null);
  const [secondaryFeature, setRawSecondaryFeature] =
    useState<SecondaryFeatureDto | null>(null);
  const [tertiaryFeature, setRawTertiaryFeature] =
    useState<TertiaryFeatureDto | null>(null);
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const { data: principalFeatures } = usePrincipalFeatures(currentPrincipal.id);

  const setPrimaryFeature = (
    feature: PrimaryFeatureDto | PrimaryFeature | null,
  ) => {
    const newFeature =
      typeof feature === 'string'
        ? principalFeatures?.find((f) => f.id === feature)
        : feature;

    setRawPrimaryFeature(newFeature ?? null);
    setSecondaryFeature(
      newFeature?.secondaryFeatures?.length
        ? newFeature.secondaryFeatures[0]
        : null,
    );
  };

  const setSecondaryFeature = (
    feature: SecondaryFeatureDto | SecondaryFeature | null,
  ) => {
    const newFeature =
      typeof feature === 'string'
        ? primaryFeature?.secondaryFeatures?.find((f) => f.id === feature)
        : feature;

    if (newFeature?.id === secondaryFeature?.id) {
      return;
    }

    setRawSecondaryFeature(newFeature ?? null);
    setRawTertiaryFeature(
      newFeature?.tertiaryFeatures?.length
        ? newFeature.tertiaryFeatures[0]
        : null,
    );
  };

  const setTertiaryFeature = (
    feature: TertiaryFeatureDto | TertiaryFeature | null,
  ) => {
    const newFeature =
      typeof feature === 'string'
        ? secondaryFeature?.tertiaryFeatures?.find((f) => f.id === feature)
        : feature;
    setRawTertiaryFeature(newFeature ?? null);
  };

  return (
    <NavigationContext.Provider
      value={{
        primaryFeature,
        setPrimaryFeature,
        secondaryFeature,
        setSecondaryFeature,
        tertiaryFeature,
        setTertiaryFeature,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      'useNavigationContext must be used within a NavigationProvider',
    );
  }
  return context;
};
